import { useEffect, useMemo } from 'react';
import { useGetCandidateList } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { visibleCandidateSections } from '../../postingDetailsRules';

/**
 * The Candidates tab's sections that hold at least one candidate, from the list's
 * per-status counts. Reads the same one-row list request `useJobOverviewData` makes,
 * so the counts come from one cache entry rather than a second request shape.
 *
 * `isLoading` is only true while the counts are still empty: once any candidate is
 * known, a background refetch keeps the sections on screen instead of flashing a spinner.
 *
 * A failed count request leaves the seeded zeros behind, which would read as "no
 * candidates" — so it is reported (toast + `isError`) rather than passed off as empty.
 */
export const useCandidateSections = (jobPostingId: string) => {
  const { statistics, isLoading, isError, error } = useGetCandidateList(
    { jobPostingId, limit: 1 },
    { enabled: !!jobPostingId, refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error]);

  const sections = useMemo(() => visibleCandidateSections(statistics.tabs), [statistics.tabs]);

  return {
    sections,
    isLoading: isLoading && statistics.tabs.all === 0,
    isError,
  };
};
